import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Send, Copy, Loader2, Eye, Upload, CheckCircle2, AlertTriangle, XCircle, Clock, IdCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-700", icon: Clock },
  authorization_sent: { label: "Authorization sent", cls: "bg-blue-50 text-blue-700 border-blue-200", icon: Send },
  submitted: { label: "Candidate submitted", cls: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: CheckCircle2 },
  in_progress: { label: "Check in progress", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  cleared: { label: "Cleared", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: ShieldCheck },
  waived: { label: "Waived", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: ShieldCheck },
  flagged: { label: "Flagged — review", cls: "bg-orange-50 text-orange-700 border-orange-200", icon: AlertTriangle },
  failed: { label: "Failed", cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

export default function BackgroundCheckPanel({ employeeId, isAdmin, onCleared }) {
  const [rec, setRec] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [busy, setBusy] = useState(null);
  const [vendor, setVendor] = useState("");
  const [ref, setRef] = useState("");
  const [notes, setNotes] = useState("");
  const [ssn, setSsn] = useState(null);
  const [lastUrl, setLastUrl] = useState("");
  const [idDocs, setIdDocs] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/employees/${employeeId}/background-check`);
      setRec(r.data);
      setVendor(r.data.vendor || "");
      setRef(r.data.reference_number || "");
      setNotes(r.data.result_notes || "");
      setTemplateId(r.data.disclosure_template_id || "");
      axios.get(`${API}/employees/${employeeId}/identity-docs`).then((x) => setIdDocs(x.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load background check");
    }
  }, [employeeId]);

  useEffect(() => {
    load();
    axios.get(`${API}/form-templates`).then((r) => setTemplates((r.data.templates || []).filter((t) => t.fillable_form?.enabled))).catch(() => {});
  }, [load]);

  const sendLink = async () => {
    setBusy("send");
    try {
      const r = await axios.post(`${API}/employees/${employeeId}/background-check/send-link`, {
        send_email: true,
        disclosure_template_id: templateId || null,
      });
      setLastUrl(r.data.full_url);
      toast.success(r.data.email_sent ? "Authorization link emailed to the candidate" : "Link created — copy it below to share");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send link");
    } finally {
      setBusy(null);
    }
  };

  const update = async (patch, label) => {
    setBusy(patch.status || "save");
    try {
      const r = await axios.patch(`${API}/employees/${employeeId}/background-check`, {
        vendor: vendor || undefined,
        reference_number: ref || undefined,
        result_notes: notes || undefined,
        ...patch,
      });
      setRec(r.data);
      if (patch.status === "cleared") {
        const ol = r.data.orientation_link;
        toast.success(ol?.email_sent ? "Cleared — orientation link created and emailed to the new hire" : "Cleared — orientation link created");
        onCleared?.();
      } else {
        toast.success(label || "Saved");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const uploadReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("report");
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/employees/${employeeId}/background-check/report`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Report filed under Background Check in the employee's files");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(null);
      e.target.value = "";
    }
  };

  const revealSsn = async () => {
    try {
      const r = await axios.get(`${API}/employees/${employeeId}/background-check/ssn`);
      setSsn(r.data.ssn);
      setTimeout(() => setSsn(null), 30000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Not available");
    }
  };

  if (!rec) return null;
  const meta = STATUS_META[rec.status] || STATUS_META.not_started;
  const Icon = meta.icon;
  const cleared = rec.status === "cleared" || rec.status === "waived";
  const cand = rec.candidate;
  const canDecide = ["submitted", "in_progress", "flagged", "authorization_sent", "not_started"].includes(rec.status);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="background-check-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-teal-600" />
          <h3 className="font-semibold text-slate-900">Pre-hire Background Check</h3>
          <Badge variant="outline" className={`gap-1 ${meta.cls}`} data-testid="bg-status-badge">
            <Icon className="w-3 h-3" /> {meta.label}
          </Badge>
        </div>
        {!cleared && (
          <p className="text-xs text-amber-700 flex items-center gap-1" data-testid="bg-gate-note">
            <AlertTriangle className="w-3.5 h-3.5" /> Orientation is locked until this check is cleared
          </p>
        )}
      </div>

      {!cleared && rec.status !== "failed" && (
        <div className="grid gap-3 md:grid-cols-[1fr_auto] items-end p-3 rounded-lg bg-slate-50">
          <div>
            <Label className="text-xs">Disclosure & authorization form (from Form Templates)</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="bg-template-select"><SelectValue placeholder="No form — info & e-signature only" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={sendLink} disabled={busy === "send"} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="bg-send-link-btn">
            {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {rec.status === "not_started" ? "Send authorization link" : "Resend link"}
          </Button>
          {lastUrl && (
            <div className="md:col-span-2 flex items-center gap-2 text-xs text-slate-600">
              <code className="truncate flex-1 bg-white border rounded px-2 py-1" data-testid="bg-link-url">{lastUrl}</code>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(lastUrl); toast.success("Copied"); }} data-testid="bg-copy-link">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
          {rec.sent_at && <p className="md:col-span-2 text-xs text-slate-500">Last sent {fmt(rec.sent_at)} to {rec.candidate_email}</p>}
        </div>
      )}

      {cand?.legal_name && (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm p-3 rounded-lg border border-slate-100" data-testid="bg-candidate-info">
          <p><span className="text-slate-500">Legal name:</span> {cand.legal_name}</p>
          <p><span className="text-slate-500">DOB:</span> {cand.dob}</p>
          <p className="flex items-center gap-2">
            <span className="text-slate-500">SSN:</span> {ssn || `•••-••-${cand.ssn_last4}`}
            {isAdmin && !ssn && (
              <button className="text-xs text-teal-700 underline inline-flex items-center gap-1" onClick={revealSsn} data-testid="bg-reveal-ssn">
                <Eye className="w-3 h-3" /> reveal
              </button>
            )}
          </p>
          <p><span className="text-slate-500">Driver's license:</span> {cand.dl_number ? `${cand.dl_number} (${cand.dl_state})` : "—"}</p>
          {cand.other_names && <p><span className="text-slate-500">Other names:</span> {cand.other_names}</p>}
          <div className="sm:col-span-2">
            <span className="text-slate-500">Addresses:</span>
            <ul className="list-disc ml-5">
              {(cand.addresses || []).map((a, i) => (
                <li key={i}>{a.street}, {a.city}, {a.state} {a.zip}{a.current ? " (current)" : a.from_date || a.to_date ? ` (${a.from_date || "?"} – ${a.to_date || "?"})` : ""}</li>
              ))}
            </ul>
          </div>
          <p className="sm:col-span-2 text-xs text-slate-500">Authorization e-signed by {cand.signer_name} on {fmt(cand.signed_at)}{rec.disclosure_file_id ? " · signed disclosure filed under Background Check" : ""}</p>
        </div>
      )}

      {idDocs && idDocs.documents.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs p-3 rounded-lg border border-slate-100" data-testid="bg-identity-docs">
          <IdCard className="w-4 h-4 text-teal-600" />
          <span className="text-slate-600">ID documents:</span>
          {(idDocs.requirements || []).map((u) => {
            const docs = u.doc_keys.map((k) => idDocs.documents.find((d) => d.key === k)).filter(Boolean);
            const provided = u.satisfied_by.map((k) => docs.find((d) => d.key === k)).filter(Boolean);
            const files = provided.flatMap((d) => d.sides.map((s) => idDocs.uploaded[`${d.key}:${s}`]).filter(Boolean));
            const approved = files.length > 0 && files.every((f) => f.review_status === "approved");
            const det = files.map((f) => f.identity_details).find((x) => x && (x.document_number || x.document_title));
            const detText = det ? ` ${[det.document_title, det.document_number ? `#${det.document_number}` : "", det.expiration_date ? `exp ${det.expiration_date}` : ""].filter(Boolean).join(" ")}` : "";
            const name = u.kind === "group" ? u.label.split("(")[0].trim() : docs[0]?.label.split("(")[0].trim();
            let text;
            if (u.covered_by_list_a) text = "covered by List A document";
            else if (provided.length) text = `${provided.map((d) => d.label.split("(")[0].trim()).join(", ")} · ${approved ? `approved${detText}` : "uploaded — review in Files"}`;
            else text = u.required ? "missing" : "optional";
            const cls = u.satisfied ? (approved || u.covered_by_list_a ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200") : u.required ? "bg-amber-50 text-amber-700 border-amber-200" : "text-slate-400";
            return (
              <Badge key={u.id} variant="outline" className={cls} data-testid={`bg-identity-${u.id}`}>
                {name}: {text}
              </Badge>
            );
          })}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        <div>
          <Label className="text-xs">Vendor / agency</Label>
          <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="e.g. WI DOJ / DHS CBC" data-testid="bg-vendor-input" />
        </div>
        <div>
          <Label className="text-xs">Reference #</Label>
          <Input value={ref} onChange={(e) => setRef(e.target.value)} data-testid="bg-ref-input" />
        </div>
        <div>
          <Label className="text-xs">Result notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="bg-notes-input" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {canDecide && rec.status !== "in_progress" && (
          <Button size="sm" variant="outline" onClick={() => update({ status: "in_progress" }, "Marked in progress")} disabled={!!busy} data-testid="bg-mark-in-progress">
            Mark in progress
          </Button>
        )}
        {!cleared && (
          <>
            <Button size="sm" onClick={() => update({ status: "cleared" })} disabled={!!busy} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="bg-mark-cleared">
              {busy === "cleared" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Mark cleared &amp; send orientation
            </Button>
            <Button size="sm" variant="outline" onClick={() => update({ status: "flagged" }, "Flagged for review")} disabled={!!busy} className="text-orange-700 border-orange-300" data-testid="bg-mark-flagged">Flag</Button>
            <Button size="sm" variant="outline" onClick={() => update({ status: "failed" }, "Marked failed")} disabled={!!busy} className="text-red-700 border-red-300" data-testid="bg-mark-failed">Failed</Button>
            {isAdmin && (
              <Button size="sm" variant="ghost" onClick={() => update({ status: "waived" }, "Waived — orientation unlocked")} disabled={!!busy} className="text-slate-600" data-testid="bg-mark-waived">Waive (existing staff)</Button>
            )}
          </>
        )}
        {cleared && (
          <Button size="sm" variant="outline" onClick={() => update({}, "Saved")} disabled={!!busy} data-testid="bg-save-details">Save details</Button>
        )}
        <label className="ml-auto">
          <input type="file" className="hidden" onChange={uploadReport} data-testid="bg-report-input" />
          <Button asChild size="sm" variant="outline" className="gap-1 cursor-pointer">
            <span>{busy === "report" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {rec.report_filename ? "Replace report" : "Upload result report"}</span>
          </Button>
        </label>
      </div>
      {cleared && (
        <p className="text-xs text-emerald-700" data-testid="bg-cleared-note">
          {rec.status === "waived" ? "Waived" : "Cleared"} on {rec.date_cleared} by {rec.cleared_by_name}{rec.orientation_link_id ? " · orientation link sent automatically" : ""}
        </p>
      )}
    </div>
  );
}
